/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VlcEEComponentAddDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import Stack from "@mui/material/Stack";
import PropTypes from "prop-types";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";


import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../redux/actions";
import actionType from "../../redux/actionTypes";
import moment from "moment";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomSelectV2 from "../../components/customComponentsV2/common/select/CustomSelectV2";
import MultiselectConnectorV2 from "../../components/customComponentsV2/common/connectors/MultiselectConnectorV2";
import { Box } from "@mui/material";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import CustomTabsV2 from "../../components/customComponentsV2/common/tabs/CustomTabsV2";
import AttributesForm from "./Attributes/AttributesForm";
import SelectVehicleComponentForVehicleSystem from "./SelectVehicleComponentForVehicleSystem";

const getCopiedVehicleSelection = (eeComponentDetails) => {
  if (!eeComponentDetails) {
    return {
      selectedVSIds: [],
      vcId: "",
    };
  }

  const vehicleComponentDetails = Array.isArray(
    eeComponentDetails?.vehicle_component_details,
  )
    ? eeComponentDetails?.vehicle_component_details?.[0]
    : eeComponentDetails?.vehicle_component_details;
  const vehicleSystemDetails = Array.isArray(
    eeComponentDetails?.vehicle_system_details,
  )
    ? eeComponentDetails?.vehicle_system_details?.[0]
    : eeComponentDetails?.vehicle_system_details;
  const copiedVehicleSystemIds = (eeComponentDetails?.vehicle_systems || [])
    ?.map(Number)
    ?.filter(Number.isFinite);
  const copiedVehicleSystemId = copiedVehicleSystemIds?.[0];
  const copiedVehicleComponentId =
    eeComponentDetails?.vc_id || eeComponentDetails?.vehicle_component_id;
  const copiedVsId = Number(
    vehicleComponentDetails?.vs_id ||
    vehicleComponentDetails?.vehicle_system_id ||
    vehicleSystemDetails?.vs_id ||
    vehicleSystemDetails?.id ||
    copiedVehicleSystemId,
  );
  const copiedVcId = Number(
    vehicleComponentDetails?.vc_id ||
    vehicleComponentDetails?.vehicle_component_id ||
    vehicleComponentDetails?.id ||
    copiedVehicleComponentId,
  );

  return {
    selectedVSIds: Number.isFinite(copiedVsId)
      ? [copiedVsId]
      : copiedVehicleSystemIds,
    vcId: Number.isFinite(copiedVcId) ? copiedVcId : "",
  };
};

const normalizeCopiedSupplierNames = (supplierName) =>
  (Array.isArray(supplierName) ? supplierName : [supplierName])
    .map((supplier) =>
      supplier && typeof supplier === "object"
        ? supplier?.value ?? supplier?.name ?? supplier?.label
        : supplier,
    )
    .filter(Boolean);

const normalizeCopiedKeywordIds = (keywords) =>
  (Array.isArray(keywords) ? keywords : [])
    .map((keyword) =>
      keyword && typeof keyword === "object"
        ? keyword?.id ?? keyword?.value
        : keyword,
    )
    .filter(Boolean);

function VlcEEComponentAddDialog({
  title,
  getEEComponentsList,
  getZoneEECountList,
  selectedZone,
  selectedZoneDetails,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  selectedVariantsMany,
  setSelectedVariantsMany,
  openEEComponentAddDialog,
  variantOptionList,
  setOpenEEComponentAddDialog,
  handleEEComponentAdd,
  vsOptionsList,
  selectedEEComponentDetails,
  adminSettingsDetails,
  supplierOptionsList,
  copyEEComponentDetails,
  onCopyPrefillClear,
}) {
  const dispatch = useDispatch();
  const [assemblyNo, setAssemblyNo] = useState("");
  const [assemblyNoError, setAssemblyNoError] = useState("");
  const [supplierName, setSupplierName] = useState([]);
  const [supplierNameError, setSupplierNameError] = useState("");
  const [version, setVersion] = useState("");
  const [versionError, setVersionError] = useState("");
  const [vcId, setVcId] = useState("");
  const [vcIdError, setVcIdError] = useState(false);
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [summary, setSummary] = useState("");
  const [summaryError, setSummaryError] = useState("");
  const [selectedVSIds, setSelectedVSIds] = useState([]);
  const [keywordOptions, setKeywordOptions] = useState([]);
  const [selectedKeywordIds, setSelectedKeywordIds] = useState([]);
  const [selectedASIL, setSelectedASIL] = useState(null);
  const [attributeFormData, setAttributeFormData] = useState({
    added: [],
    edited: [],
    deleted: [],
  });
  const [addTabValue, setAddTabValue] = useState(0);
  const selectedVSIdRef = useRef("");
  const copiedVehicleSelection = useMemo(
    () => getCopiedVehicleSelection(copyEEComponentDetails),
    [copyEEComponentDetails],
  );
  const effectiveSelectedVSIds =
    openEEComponentAddDialog &&
      copyEEComponentDetails &&
      selectedVSIds.length === 0 &&
      copiedVehicleSelection.selectedVSIds.length > 0
      ? copiedVehicleSelection.selectedVSIds
      : selectedVSIds;
  const effectiveVcId =
    openEEComponentAddDialog &&
      copyEEComponentDetails &&
      !vcId &&
      copiedVehicleSelection.vcId
      ? copiedVehicleSelection.vcId
      : vcId;

  useEffect(() => {
    selectedVSIdRef.current = selectedVSIds?.[0] || "";
  }, [selectedVSIds]);

  const handleAttributeFormData = useCallback((data = {}) => {
    const nextData = {
      added: Array.isArray(data?.added) ? data.added : [],
      edited: Array.isArray(data?.edited) ? data.edited : [],
      deleted: Array.isArray(data?.deleted) ? data.deleted : [],
    };
    setAttributeFormData((prev) =>
      JSON.stringify(prev) === JSON.stringify(nextData) ? prev : nextData,
    );
  }, []);

  const addedAttributeFormRows = useMemo(
    () =>
      (Array.isArray(attributeFormData?.added)
        ? attributeFormData.added
        : []
      ).map((attribute) => ({
        ...attribute,
        attribute_id: null,
        newlyAdded: true,
        edited: false,
      })),
    [attributeFormData?.added],
  );

  const handleSelectedVehicleSystem = useCallback((vehicleSystemId = "") => {
    const currentVehicleSystemId = selectedVSIdRef.current;
    const nextVehicleSystemId = vehicleSystemId;

    if (String(currentVehicleSystemId) !== String(nextVehicleSystemId)) {
      setVcId("");
    }
    setSelectedVSIds((prev) => {
      const prevVehicleSystemId = prev?.[0] || "";
      if (String(prevVehicleSystemId) === String(nextVehicleSystemId)) {
        return prev;
      }

      return nextVehicleSystemId ? [nextVehicleSystemId] : [];
    });
    setVcIdError(false);
  }, []);

  const handleSelectedVehicleComponent = useCallback((vehicleComponentId = "") => {
    setVcId(vehicleComponentId);
    setVcIdError(false);
  }, []);


  useEffect(() => {
    if (!openEEComponentAddDialog) return;

    dispatch(
      HandleApiActions({
        api_name: "/SCM/settings/view/keywords",
        https_method: "GET",
        params: { type: 2 },
        ignore_app_loading_spinner: true,
      }),
    ).then((response) => {
      const keywords = Array.isArray(response?.data) ? response.data : [];
      setKeywordOptions(
        keywords.map((keyword) => ({
          value: keyword?.id,
          label: keyword?.name,
        })),
      );
    });
  }, [dispatch, openEEComponentAddDialog]);

  useEffect(() => {
    if (!openEEComponentAddDialog || !copyEEComponentDetails) return;

    const currentVariantId = selectedSingleVariantDetails?.variant_id;
    const copiedVariantIds = Array.isArray(copyEEComponentDetails?.variant_ids)
      ? copyEEComponentDetails.variant_ids
      : [];
    const nextVariantIds = [
      ...new Set(
        [currentVariantId, ...copiedVariantIds].filter(Boolean),
      ),
    ];
    const copiedAttributes = Array.isArray(copyEEComponentDetails?.attributes)
      ? copyEEComponentDetails.attributes.map((attribute) => ({
        attribute_type: attribute?.attribute_type,
        attribute_sub_type: attribute?.attribute_sub_type || null,
        attribute_heading: attribute?.attribute_heading,
        attribute_value: attribute?.attribute_value,
      }))
      : [];

    setAssemblyNo("");
    setAssemblyNoError("");
    setSupplierName(
      normalizeCopiedSupplierNames(copyEEComponentDetails?.supplier_name),
    );
    setSupplierNameError("");
    setVersion(copyEEComponentDetails?.version || "");
    setVersionError("");
    setDescription(copyEEComponentDetails?.description || "");
    setDescriptionError("");
    setSummary(copyEEComponentDetails?.summary || "");
    setSummaryError("");
    setSelectedVSIds(copiedVehicleSelection.selectedVSIds);
    setVcId(copiedVehicleSelection.vcId);
    setVcIdError(false);
    setSelectedKeywordIds(
      normalizeCopiedKeywordIds(copyEEComponentDetails?.keywords),
    );
    setSelectedASIL(copyEEComponentDetails?.other_fields?.asil_class || null);
    setSelectedVariantsMany(nextVariantIds);
    setAttributeFormData({
      added: copiedAttributes,
      edited: [],
      deleted: [],
    });
    setAddTabValue(0);
  }, [
    copyEEComponentDetails,
    copiedVehicleSelection,
    openEEComponentAddDialog,
    selectedSingleVariantDetails?.variant_id,
    setSelectedVariantsMany,
  ]);

  useEffect(() => {
    if (
      openEEComponentAddDialog &&
      selectedSingleVariantDetails?.variant_id != null
    ) {
      const id = selectedSingleVariantDetails.variant_id;
      setSelectedVariantsMany((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const hasId = prevArr.map(String).includes(String(id));
        if (hasId) return prevArr;
        return [id, ...prevArr];
      });
    }
    setAddTabValue(0);
  }, [
    openEEComponentAddDialog,
    selectedSingleVariantDetails,
    setSelectedVariantsMany,
  ]);


  const clearAddEEComponentFields = () => {
    setAssemblyNo("");
    setAssemblyNoError("");
    setSupplierName([]);
    setSupplierNameError("");
    setVersion("");
    setVersionError("");
    setDescription("");
    setDescriptionError("");
    setSummary("");
    setSummaryError("");
    setVcId("");
    setVcIdError(false);
    setSelectedVSIds([]);
    setSelectedKeywordIds([]);
    setSelectedASIL(null);
    setSelectedVariantsMany([]);
    setAttributeFormData({ added: [], edited: [], deleted: [] });
  };

  const isAddEEComponentValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;
    return (
      assemblyNo?.trim() !== "" &&
      assemblyNo?.trim().length >= 3 &&
      !assemblyNoError &&
      regexMin3.test(assemblyNo.trim()) &&
      supplierName?.length > 0 &&
      !supplierNameError &&
      version?.trim() !== "" &&
      version?.trim().length >= 3 &&
      !versionError &&
      regexMin3.test(version.trim()) &&
      description?.trim() !== "" &&
      description?.trim().length >= 3 &&
      !descriptionError &&
      selectedASIL?.length > 0 &&
      !vcIdError &&
      (summary?.trim().length === 0 || !summaryError) &&
      (summary?.trim().length === 0 || summary.trim().length >= 3)
    );
  };

  const isAddEEComponentDraftValid = () => {
    return (
      assemblyNo?.trim() !== "" ||
      supplierName?.length > 0 ||
      version?.trim() !== "" ||
      description?.trim() !== "" ||
      summary?.trim() !== "" ||
      effectiveSelectedVSIds?.length > 0 ||
      Boolean(effectiveVcId) ||
      selectedKeywordIds?.length > 0 ||
      selectedASIL?.length > 0
    );
  };

  const handleCloseEEComponentAdd = () => {
    setOpenEEComponentAddDialog(false);

    clearAddEEComponentFields();
    onCopyPrefillClear?.();
  };

  const handleAddEEComponent = () => {
    handleEEComponentAdd({
      variant_ids: selectedVariantsMany,
      save_as_draft: false,
      assembly_number: assemblyNo?.trim(),
      supplier_name: supplierName,
      version: version?.trim(),
      keywords: selectedKeywordIds?.map(Number) || [],
      summary: summary,
      ...(effectiveVcId ? { vc_id: Number(effectiveVcId) } : null),
      description: description,
      ...(effectiveSelectedVSIds?.length > 0
        ? { vehicle_systems: effectiveSelectedVSIds?.map(Number) || [] }
        : null),
      other_fields: {
        asil_class: selectedASIL,
      },
      attributes: attributeFormData?.added || [],
    });
    handleCloseEEComponentAdd();
  };

  const handleAddEEComponentDraft = () => {
    const reqObj = {
      project_id: selectedProjectDetails?.project_id,
      variant_id: selectedSingleVariantDetails?.variant_id,

      ...(selectedProjectDetails?.is_zonal && {
        zone_position: selectedProjectDetails?.project_zones?.find(
          (zones) => zones?.zone_name === selectedZone?.zone_position_name,
        )?.position,
      }),
      variant_ids: selectedVariantsMany,
      save_as_draft: true,
      assembly_number: assemblyNo?.trim(),
      ...(supplierName?.length > 0 ? { supplier_name: supplierName } : null),

      ...(version ? { version: version?.trim() } : null),
      ...(summary ? { summary: summary } : null),
      ...(description ? { description: description } : null),
      keywords: selectedKeywordIds?.map(Number) || [],
      ...(effectiveSelectedVSIds?.length > 0
        ? { vehicle_systems: effectiveSelectedVSIds?.map(Number) || [] }
        : null),
      ...(effectiveVcId ? { vc_id: Number(effectiveVcId) } : null),
      ...(selectedASIL?.length > 0
        ? {
          other_fields: {
            asil_class: selectedASIL,
          },
        }
        : null),
      attributes: attributeFormData?.added || [],
      current_time: moment().format(),
    };

    dispatch(
      HandleApiActions({
        api_name: "/VLC/ee_components/add",
        https_method: "POST",
        action_type: actionType.Add_EE_COMPONENT,
        description: "Add in Save As Draft EE Component",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      getEEComponentsList();
      handleCloseEEComponentAdd();
    });
  };

  return (
    <Box>

      <CustomDialogBoxV2
        open={openEEComponentAddDialog}
        onClose={() => {
          setOpenEEComponentAddDialog(false);
          clearAddEEComponentFields();
          onCopyPrefillClear?.();
        }}
        title="Add EE Component"
        onClickSuccess={() => handleAddEEComponentDraft()}
        onClickReject={() => setOpenEEComponentAddDialog(false)}
        dialogActions={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Box>
              {addTabValue === 1 ? (
                <CustomButtonV2
                  type="cancel"
                  size="small"
                  btnName={"Back"}
                  handleOnClick={() => setAddTabValue(0)}
                />
              ) : null}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {addTabValue === 1 ? (
                <CustomButtonV2
                  type="other"
                  size="small"
                  disabled={!isAddEEComponentDraftValid()}
                  btnName={"Save Draft"}
                  handleOnClick={() => {
                    handleAddEEComponentDraft();
                  }}
                />
              ) : null}

              <CustomButtonV2
                type="submit"
                size="small"
                disabled={
                  addTabValue === 0
                    ? !isAddEEComponentDraftValid()
                    : !isAddEEComponentValid()
                }
                btnName={addTabValue === 0 ? "Next" : "Add"}
                handleOnClick={() => {
                  if (addTabValue === 0) {
                    setAddTabValue(1);
                  } else {
                    handleAddEEComponent();
                  }
                }}
              />
            </Box>
          </Box>
        }
      >
        <CustomTabsV2
          defaultTab={addTabValue}
          value={addTabValue}
          onTabChange={(newValue) => setAddTabValue(newValue)}
          tabs={[
            {
              name: "Details",
              content: (
                <Stack spacing={2} mb={2} sx={{ width: "450px" }}>
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Assembly No"}
                    required={true}
                    type={"text"}
                    fldType={"textR"}
                    value={assemblyNo}
                    setValue={(value) =>
                      value.length <= 40 && setAssemblyNo(value)
                    }
                    maxLength={40}
                  />
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Version"}
                    required={true}
                    type={"text"}
                    fldType="textR"
                    value={version}
                    setValue={(value) =>
                      value.length <= 40 && setVersion(value)
                    }
                    maxLength={40}
                  />
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Description"}
                    required={true}
                    value={description}
                    setValue={(value) =>
                      value.length <= 500 && setDescription(value)
                    }
                    maxLength={500}
                  />
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Summary"}
                    type={"text"}
                    fldType={"textO"}
                    value={summary}
                    setValue={(value) =>
                      value.length <= 250 && setSummary(value)
                    }
                    valueError={summaryError}
                    setValueError={setSummaryError}
                    maxLength={250}
                  />
                  <MultiselectConnectorV2
                    multiple
                    selectedValues={supplierName || []}
                    setSelectedValues={setSupplierName}
                    label={"Select Supplier Name"}
                    optionsList={supplierOptionsList}
                    placeholder={false}
                  />

                  <SelectVehicleComponentForVehicleSystem
                    open={openEEComponentAddDialog}
                    projectId={selectedProjectDetails?.project_id}
                    variantId={selectedSingleVariantDetails?.variant_id}
                    isZonal={selectedProjectDetails?.is_zonal}
                    zonePosition={selectedZoneDetails?.position}
                    getSelectedVehicleSystem={handleSelectedVehicleSystem}
                    getSelectedVehicleComponent={handleSelectedVehicleComponent}
                    setVcIdError={setVcIdError}
                    vsId={effectiveSelectedVSIds[0]}
                    vcId={effectiveVcId}
                  />
                  <CustomSelectV2
                    size={"small"}
                    label={"Select ASIL Class"}
                    required={true}
                    optionsList={["A", "B", "C", "D"]?.map((el) => ({
                      value: el,
                      label: el,
                    }))}
                    selectedValue={selectedASIL || " "}
                    handleOnChange={(e) => setSelectedASIL(e.target.value)}
                  />
                  <MultiselectConnectorV2
                    multiple
                    selectedValues={selectedKeywordIds || []}
                    setSelectedValues={setSelectedKeywordIds}
                    label={"Keywords"}
                    optionsList={keywordOptions}
                    placeholder={false}
                  />
                  <MultiselectConnectorV2
                    multiple
                    selectedValues={selectedVariantsMany || " "}
                    setSelectedValues={setSelectedVariantsMany}
                    label={"Select Variants"}
                    optionsList={variantOptionList}
                    placeholder={false}
                    immutableValues={
                      selectedSingleVariantDetails?.variant_id == null
                        ? []
                        : [selectedSingleVariantDetails?.variant_id]
                    }
                  />
                </Stack>
              ),
            },
            {
              name: "Attribute",
              content: (
                <Stack
                  spacing={2}
                  mb={2}
                  justifyContent={"center"}
                  sx={{ width: "450px" }}
                >
                  <AttributesForm
                    type={"ADD"}
                    attributeFromApi={addedAttributeFormRows}
                    getFormData={handleAttributeFormData}
                  />
                </Stack>
              ),
            },
          ]}
        />
      </CustomDialogBoxV2>
    </Box>
  );
}

VlcEEComponentAddDialog.propTypes = {
  title: PropTypes.string,
  getEEComponentsList: PropTypes.func,
  getZoneEECountList: PropTypes.func,
  selectedZone: PropTypes.shape({
    zone_position_name: PropTypes.string,
  }),
  selectedZoneDetails: PropTypes.shape({
    position: PropTypes.any,
  }),
  selectedProjectDetails: PropTypes.shape({
    project_id: PropTypes.any,
    is_zonal: PropTypes.bool,
    project_zones: PropTypes.arrayOf(
      PropTypes.shape({
        zone_name: PropTypes.string,
        position: PropTypes.any,
      }),
    ),
  }),
  selectedSingleVariantDetails: PropTypes.shape({
    variant_id: PropTypes.any,
  }),
  selectedVariantsMany: PropTypes.any,
  setSelectedVariantsMany: PropTypes.func,
  openEEComponentAddDialog: PropTypes.bool,
  variantOptionList: PropTypes.array,
  setOpenEEComponentAddDialog: PropTypes.func,
  handleEEComponentAdd: PropTypes.func,
  vsOptionsList: PropTypes.array,
  selectedEEComponentDetails: PropTypes.any,
  adminSettingsDetails: PropTypes.any,
  supplierOptionsList: PropTypes.array,
  copyEEComponentDetails: PropTypes.shape({
    supplier_name: PropTypes.any,
    keywords: PropTypes.any,
    variant_ids: PropTypes.array,
    attributes: PropTypes.arrayOf(
      PropTypes.shape({
        attribute_type: PropTypes.any,
        attribute_sub_type: PropTypes.any,
        attribute_heading: PropTypes.any,
        attribute_value: PropTypes.any,
      }),
    ),
    version: PropTypes.string,
    description: PropTypes.string,
    summary: PropTypes.string,
    other_fields: PropTypes.shape({
      asil_class: PropTypes.any,
    }),
  }),
  onCopyPrefillClear: PropTypes.func,
};

export default VlcEEComponentAddDialog;
